import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';
import TrashCan from '@gef-ui/material-icons/TrashCan';

import { ComponentLoader } from 'components';
import CollapsibleTable from 'components/atoms/GefMui/CollapsibleTable';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import { USER_ROLES } from 'constants/general';
import fetch from 'features/common/fetch';
import { loadMore, loadPage, reload, sortRecords, submitDocuments } from 'features/views/production/documents/actions';
import { getDocuments, getSort } from 'features/views/production/documents/selectors';
import { getCodelistByName } from 'utils';

import DocumentActionButtons from './DocumentActionButtons';
import DocumentsTableRow from './DocumentsTableRow';

const fetchStorePath = `${fetch.constants.urls.productionDocuments}.overview`;

const DocumentsTable = ({ role }) => {
	const dispatch = useDispatch();

	const documents = useSelector(getDocuments);
	const fetchState = useSelector(fetch.selectors.getFetchState(fetchStorePath));
	const sort = useSelector(getSort);
	const ppDocumentType = useSelector(getCodelistByName(codelists.ppDocumentType));

	useEffect(() => {
		if (isEmpty(documents) && role !== USER_ROLES.CENTRAL) {
			dispatch(submitDocuments());
		}
		// eslint-disable-next-line
	}, []);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPage(page - 1));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(reload());
	}, [dispatch]);

	const handleSort = useCallback(
		(sort) => {
			dispatch(sortRecords(sort));
		},
		[dispatch],
	);

	const hasRecords = !isEmpty(documents);

	return (
		<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleReload}>
			<Stack spacing={2}>
				<CollapsibleTable
					tableId="DocumentsTable"
					autoCloseOtherRows
					tableHeader={
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>{getLabelMessage('producedProduction:client')}</TableCell>
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="reportedDate"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('producedProduction:reportDate')}
									</SortCell>
								</TableCell>
								<TableCell>{getLabelMessage('producedProduction:policyNo')}</TableCell>
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="documentTypeText"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('producedProduction:document')}
									</SortCell>
								</TableCell>
								<TableCell>{getLabelMessage('producedProduction:scan')}</TableCell>
								<TableCell>{getLabelMessage('producedProduction:name')}</TableCell>
								<TableCell>
									<TrashCan sx={{ ml: 1 }} />
								</TableCell>
							</TableRow>
						</TableHead>
					}
					tableBody={
						!hasRecords ? (
							<TableHasNoResults fetchState={fetchState} />
						) : (
							documents.map((doc) => (
								<DocumentsTableRow
									key={doc.id}
									id={doc.id}
									doc={doc}
									role={role}
									ppDocumentType={ppDocumentType}
								/>
							))
						)
					}
				>
					<PaginationRow
						fetchStorePath={fetchStorePath}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
					/>
				</CollapsibleTable>
				<DocumentActionButtons role={role} />
			</Stack>
		</ComponentLoader>
	);
};

DocumentsTable.propTypes = {
	role: PropTypes.string,
};

export default DocumentsTable;
